(() => {
  "use strict";

  const CONFIG = Object.freeze({
    volume: {
      units: [
        { key: "mL", factor: 1 },
        { key: "L", factor: 1000 },
        { key: "tsp", factor: 4.92892159375 },
        { key: "tbsp", factor: 14.78676478125 },
        { key: "fl oz", factor: 29.5735295625 },
        { key: "cup", factor: 236.5882365 },
        { key: "gal", factor: 3785.411784 }
      ],
      toggle: ["mL", "L", "fl oz", "cup"]
    },
    mass: {
      units: [
        { key: "kg", factor: 1000 },
        { key: "lb", factor: 453.59237 },
        { key: "g", factor: 1 },
        { key: "oz", factor: 28.349523125 }
      ],
      toggle: ["kg", "lb", "g", "oz"]
    },
    length: {
      units: [
        { key: "mm", factor: 0.1 },
        { key: "cm", factor: 1 },
        { key: "m", factor: 100 },
        { key: "in", factor: 2.54 },
        { key: "ft", factor: 30.48 }
      ],
      toggle: ["cm", "m", "in", "ft"]
    }
  });

  const TEMPERATURE_UNITS = Object.freeze(["°C", "°F"]);

  class UnitConverter extends HTMLElement {
    constructor() {
      super();
      this.controllers = [];
    }

    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.controllers = Array.from(this.querySelectorAll("[data-converter]"))
        .map((card) => this.buildConverter(card))
        .filter(Boolean);
    }

    disconnectedCallback() {
      this.controllers.forEach((controller) => controller.destroy());
      this.controllers = [];
      this.dataset.initialized = "false";
    }

    buildConverter(card) {
      const type = card.dataset.converter;
      const input = card.querySelector("[data-value]");
      const toggle = card.querySelector("[data-toggle]");
      const results = card.querySelector("[data-results]");

      if (!input || !toggle || !results) return null;

      if (type === "temperature") {
        return this.buildTemperatureConverter(input, toggle, results);
      }

      const config = CONFIG[type];
      if (!config) return null;

      return this.buildLinearConverter(input, toggle, results, config);
    }

    buildTemperatureConverter(input, toggle, results) {
      let selectedUnit = TEMPERATURE_UNITS[0];

      const render = () => {
        results.innerHTML = "";
        const value = Number.parseFloat(input.value);
        const hasValue = Number.isFinite(value);
        const celsius = hasValue ? this.toCelsius(value, selectedUnit) : null;

        TEMPERATURE_UNITS.forEach((unit) => {
          if (unit === selectedUnit) return;

          const output = celsius === null
            ? null
            : this.fromCelsius(celsius, unit);

          const tile = this.createTile(unit, this.formatNumber(output));

          if (celsius !== null && (celsius >= 39.7 || celsius <= 37.5)) {
            tile.classList.add("is-warning");
          }

          results.appendChild(tile);
        });
      };

      const onToggleClick = (event) => {
        const button = event.target.closest("button[data-unit]");
        if (!button || !toggle.contains(button)) return;

        selectedUnit = button.dataset.unit;
        this.setActiveButton(toggle, selectedUnit);
        render();
      };

      TEMPERATURE_UNITS.forEach((unit) => {
        toggle.appendChild(this.createUnitButton(unit, unit === selectedUnit));
      });

      input.addEventListener("input", render);
      toggle.addEventListener("click", onToggleClick);
      render();

      return {
        destroy: () => {
          input.removeEventListener("input", render);
          toggle.removeEventListener("click", onToggleClick);
        }
      };
    }

    buildLinearConverter(input, toggle, results, config) {
      let selectedUnit = config.toggle[0];
      const factorMap = new Map(config.units.map((unit) => [unit.key, unit.factor]));

      const render = () => {
        results.innerHTML = "";
        const value = Number.parseFloat(input.value);
        const hasValue = Number.isFinite(value);
        const selectedFactor = factorMap.get(selectedUnit);
        const baseValue = hasValue ? value * selectedFactor : null;

        config.units.forEach((unit) => {
          if (unit.key === selectedUnit) return;

          const converted = baseValue === null
            ? null
            : baseValue / unit.factor;

          results.appendChild(
            this.createTile(unit.key, this.formatNumber(converted))
          );
        });
      };

      const onToggleClick = (event) => {
        const button = event.target.closest("button[data-unit]");
        if (!button || !toggle.contains(button)) return;

        selectedUnit = button.dataset.unit;
        this.setActiveButton(toggle, selectedUnit);
        render();
      };

      config.toggle.forEach((unit) => {
        toggle.appendChild(this.createUnitButton(unit, unit === selectedUnit));
      });

      input.addEventListener("input", render);
      toggle.addEventListener("click", onToggleClick);
      render();

      return {
        destroy: () => {
          input.removeEventListener("input", render);
          toggle.removeEventListener("click", onToggleClick);
        }
      };
    }

    createUnitButton(unit, active) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.unit = unit;
      button.textContent = unit;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
      return button;
    }

    setActiveButton(container, selectedUnit) {
      container.querySelectorAll("button[data-unit]").forEach((button) => {
        const active = button.dataset.unit === selectedUnit;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });
    }

    createTile(unit, value) {
      const tile = document.createElement("div");
      tile.className = "uc-tile";

      const unitElement = document.createElement("div");
      unitElement.className = "uc-tile-unit";
      unitElement.textContent = unit;

      const valueElement = document.createElement("div");
      valueElement.className = "uc-tile-value";
      valueElement.textContent = value;

      tile.append(unitElement, valueElement);
      return tile;
    }

    toCelsius(value, unit) {
      return unit === "°C" ? value : (value - 32) * 5 / 9;
    }

    fromCelsius(celsius, unit) {
      return unit === "°C" ? celsius : celsius * 9 / 5 + 32;
    }

    formatNumber(value) {
      if (value === null || !Number.isFinite(value)) return "—";
      if (Object.is(value, -0) || value === 0) return "0";

      const absolute = Math.abs(value);
      if (absolute >= 1e9 || absolute < 1e-6) {
        return value.toExponential(4);
      }

      const significantDigits = 7;
      const rounded = Number.parseFloat(value.toPrecision(significantDigits));
      return String(rounded);
    }
  }

  if (!customElements.get("s-unit-converter")) {
    customElements.define("s-unit-converter", UnitConverter);
  }
})();
